import {
  NewNotification,
  NotificationHistory,
  NotificationItem,
  ReporterAccessFlags,
  UserAccessFlags,
  UserProfile,
  newNotificationSchema,
  notificationHistorySchema,
} from './types';

import { restAPI } from '../rest-api';

import { stripUndefined } from '~/utils/helpers';
import { AtLeastOne } from '~/utils/type-helpers';
import { stringToJSONSchema } from '~/utils/zod-schema';

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    currentUser: builder.query<UserProfile, void>({
      query: () => ({ url: '/user/profile' }),
    }),
    updateUserProfile: builder.mutation<
      UserProfile,
      AtLeastOne<Omit<UserProfile, 'id'>>
    >({
      query: (payload) => ({
        url: '/user/profile/update',
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Tenant', id: 'user-list' },
              { type: 'Tenant', id: 'intake-channels' },
              { type: 'Tenant', id: 'user-tenant' },
            ]
          : [],
    }),
    userTypes: builder.query<
      Array<{
        value: number;
        type: string;
      }>,
      void
    >({
      query: () => ({ url: '/user-types' }),
      providesTags: (res) => (res ? [{ type: 'Users', id: 'types' }] : []),
    }),
    flags: builder.query<UserAccessFlags, void>({
      query: () => ({ url: '/user/flags' }),
      providesTags: (res) => (res ? [{ type: 'Users', id: 'flags' }] : []),
    }),
    reporterFlags: builder.query<ReporterAccessFlags, void>({
      query: () => ({ url: '/flags' }),
      providesTags: (res) =>
        res ? [{ type: 'Users', id: 'reporter-flags' }] : [],
    }),
    updateReporterFlags: builder.mutation<
      ReporterAccessFlags,
      AtLeastOne<ReporterAccessFlags>
    >({
      query: (payload) => ({
        url: '/flags',
        method: 'PATCH',
        data: stripUndefined(payload),
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Users', id: 'reporter-flags' }] : [],
    }),
    updateUserFlags: builder.mutation<
      UserAccessFlags,
      AtLeastOne<UserAccessFlags>
    >({
      query: (payload) => ({
        url: '/user/flags',
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Users', id: 'flags' },
              { type: 'Tenant', id: 'department-list' },
            ]
          : [],
    }),
    resendInvitationEmail: builder.mutation<void, { userId: number }>({
      query: ({ userId }) => ({
        url: `/resend-invite-email/${userId}/resend-email`,
        method: 'POST',
      }),
      invalidatesTags: (res, _err, arg) =>
        res
          ? [{ type: 'Tenant', id: `tenant-user:${arg.userId}-role-settings` }]
          : [],
    }),
    sendPasswordResetEmail: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: '/send-reset-password-email',
        method: 'POST',
        data: { email },
      }),
    }),
    userNotifications: builder.query<NotificationHistory, void>({
      queryFn() {
        return {
          data: { notifications: [], type: 'notification.history', user_id: 0 },
        };
      },
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const WS_PATH = import.meta.env.VITE_WS_PATH;
        const url = `${
          import.meta.env.VITE_WSS_PROTOCOL
        }${WS_PATH}/notifications/`;
        // create a websocket connection when the cache subscription starts
        const ws = new WebSocket(url);
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          // when data is received from the socket connection to the server,
          // if it is a message and for the appropriate channel,
          // update our query result with the received message
          const listener = (event: MessageEvent) => {
            const recievedData = stringToJSONSchema.catch({}).parse(event.data);
            const notificationType = newNotificationSchema
              .pick({ type: true })
              .safeParse(recievedData);

            const toNotificationItem = (
              data: NewNotification,
            ): NotificationItem => {
              return {
                content: data.content,
                model_type: data.model_type,
                record_obj: data.record_obj,
                created_at: data.created_at,
                recipients: data.recipients,
              };
            };

            if (!notificationType.success) return;
            const newNotification =
              newNotificationSchema.safeParse(recievedData);
            if (
              notificationType.data.type !== 'notification.message' &&
              newNotification.success
            ) {
              // Transform to notification item shape  and append to cache
              updateCachedData((draft) => {
                draft.notifications = [
                  toNotificationItem(newNotification.data),
                  ...draft.notifications,
                ];
              });
            } else if (notificationType.data.type === 'notification.history') {
              const parsed = notificationHistorySchema.safeParse(recievedData);
              if (parsed.success) {
                updateCachedData(() => {
                  parsed.data.notifications.reverse();
                  return parsed.data;
                });
              }
            }
          };

          ws.addEventListener('message', listener);
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        ws.close();
      },
    }),
  }),
  overrideExisting: false,
});

export const useCurrentUser = endpoints.endpoints.currentUser.useQuery;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const UserService = endpoints;
